// What the issue views derive from the read API: status against the previous release and the
// issue's state (D100), the stack with the crashing in-app frame, and device and OS
// breakdowns. Issues come from the symbolicator's occurrences (D73): every kind, symbolicated
// frames (function, file, line, in_app) and the raw stack as sent beside them.
import type {
  IssueDetail,
  IssueState,
  IssueStateChange,
  IssueSummary,
  RawFrame,
  SymbolicatedFrame,
} from '../api/types'
import { isSystemImage } from './conventions'

export type IssueStatus = 'new' | 'ongoing'

/** What a row's glyph says: ● new · ◐ regressed · ○ ongoing · ✓ resolved · – ignored. */
export type IssueGlyph = IssueStatus | 'regressed' | 'resolved' | 'ignored'

/**
 * The state wins over newness: a resolved or ignored issue reads as such, and an unresolved one
 * that came back in a newer release reads as regressed; otherwise new or ongoing.
 */
export function glyphOf(
  issue: IssueSummary,
  previousIds: Set<string> | undefined,
  release?: string,
): IssueGlyph {
  const state = issue.state
  if (state?.status === 'resolved') return 'resolved'
  if (state?.status === 'ignored') return 'ignored'
  if (state?.regressed_release) return 'regressed'
  return statusOf(issue, previousIds, release)
}

export const HOUR = 3_600_000

/** The ignores the console offers: forever, until more occurrences, or for a while. */
export type IgnorePreset =
  | { mode: 'forever' }
  | { mode: 'count'; count: number }
  | { mode: 'until'; hours: number }

export function ignoreChange(preset: IgnorePreset, now = Date.now()): IssueStateChange {
  if (preset.mode === 'count')
    return { status: 'ignored', ignore: { mode: 'count', count: preset.count } }
  if (preset.mode === 'until')
    return {
      status: 'ignored',
      ignore: { mode: 'until', until: new Date(now + preset.hours * HOUR).toISOString() },
    }
  return { status: 'ignored', ignore: { mode: 'forever' } }
}

export const RESOLVE_NEXT: IssueStateChange = { status: 'resolved', resolution: 'next_release' }
export const REOPEN: IssueStateChange = { status: 'unresolved' }

/**
 * The change that puts an issue back as it was before `change` (D121: Undo, not ask, for resolve,
 * ignore and assign). An ignore by count starts counting again.
 */
export function undoChange(
  prev: IssueState | null | undefined,
  change: IssueStateChange,
): IssueStateChange {
  if (!('status' in change) || change.status === undefined)
    return { assignee_id: prev?.assignee_id ?? null }
  if (!prev || prev.status === 'unresolved') return REOPEN
  if (prev.status === 'resolved')
    return prev.resolution === 'in_release' && prev.resolved_app_id && prev.resolved_release
      ? {
          status: 'resolved',
          resolution: 'in_release',
          app_id: prev.resolved_app_id,
          release: prev.resolved_release,
        }
      : RESOLVE_NEXT
  const ignore = prev.ignore
  if (ignore?.mode === 'count' && ignore.count !== null)
    return { status: 'ignored', ignore: { mode: 'count', count: ignore.count } }
  if (ignore?.mode === 'until' && ignore.until)
    return { status: 'ignored', ignore: { mode: 'until', until: ignore.until } }
  return { status: 'ignored', ignore: { mode: 'forever' } }
}

/** Undo for several issues at once: one change per group of issues that were alike. */
export function undoGroups(
  prev: readonly { id: string; state?: IssueState | null }[],
  change: IssueStateChange,
): { change: IssueStateChange; ids: string[] }[] {
  const groups = new Map<string, { change: IssueStateChange; ids: string[] }>()
  for (const p of prev) {
    const back = undoChange(p.state, change)
    const key = JSON.stringify(back)
    const g = groups.get(key) ?? { change: back, ids: [] }
    g.ids.push(p.id)
    groups.set(key, g)
  }
  return [...groups.values()]
}

export { typingIn } from '@apsio/ui'

/**
 * New when it occurs in `release` and the release before it did not have it (an empty set when
 * `release` is the first); ongoing otherwise, or when the previous release is not known yet.
 */
export function statusOf(
  issue: IssueSummary,
  previousIds: Set<string> | undefined,
  release?: string,
): IssueStatus {
  // The release an issue was first seen in (APS-299) answers it outright.
  if (release !== undefined && issue.first_seen_release)
    return issue.first_seen_release === release ? 'new' : 'ongoing'
  if (!previousIds) return 'ongoing'
  if (release !== undefined && !issue.releases.includes(release)) return 'ongoing'
  return previousIds.has(issue.id) ? 'ongoing' : 'new'
}

/** The pill tone of an issue kind: crash red; hang, ANR and abnormal exits amber; errors neutral. */
export function kindTone(kind: string): 'crash' | 'warn' | 'neutral' {
  if (kind === 'crash') return 'crash'
  if (kind === 'hang' || kind === 'anr' || kind === 'abnormal_exit') return 'warn'
  return 'neutral'
}

export interface FrameView {
  index: number
  /** The function, when symbolicated; the address otherwise. */
  symbol: string
  /** file:line (the file's name only), or the image. */
  where: string
  /** The full source path as the symbols recorded it, when there is one. */
  path?: string
  system: boolean
}

/** The last segment of a source path, for either separator. */
export const baseName = (path: string): string => path.split(/[\\/]/).pop() || path

/** A symbolicated frame: function and file:line when symbols matched, the address otherwise. */
export function frameView(frame: SymbolicatedFrame, position: number): FrameView {
  const line = frame.line ? `:${frame.line}` : ''
  return {
    index: position,
    symbol: frame.function || frame.address || frame.image,
    where: frame.file ? `${baseName(frame.file)}${line}` : frame.image,
    ...(frame.file ? { path: `${frame.file}${line}` } : {}),
    system: !frame.in_app,
  }
}

/** A frame as the device sent it, before symbolication. */
export function rawFrameView(frame: RawFrame, position: number): FrameView {
  const address =
    frame.address !== null
      ? `${frame.address}${frame.offset !== null ? ` + ${frame.offset}` : ''}`
      : undefined
  return {
    index: frame.index ?? position,
    symbol: address ?? frame.raw,
    where: frame.image ?? '',
    system: isSystemImage(frame.image),
  }
}

export interface StackView {
  frames: FrameView[]
  /** Index of the first in-app frame, the one to highlight; -1 when there is none. */
  crashing: number
  systemCount: number
  /** Which stack is shown: the symbolicated one, or the raw one as a fallback. */
  source: 'symbolicated' | 'raw'
}

/**
 * The symbolicated stack when the occurrence is symbolicated (or no raw stack exists); the raw
 * stack as sent otherwise, so an unsymbolicated crash still shows images and addresses.
 */
export function stackOf(
  latest: Pick<IssueDetail['latest'], 'symbolicated' | 'frames' | 'raw_frames'>,
): StackView {
  const symbolicated =
    latest.frames.length > 0 && (latest.symbolicated || latest.raw_frames.length === 0)
  const frames = symbolicated ? latest.frames.map(frameView) : latest.raw_frames.map(rawFrameView)
  return {
    frames,
    crashing: frames.findIndex((f) => !f.system),
    systemCount: frames.filter((f) => f.system).length,
    source: symbolicated ? 'symbolicated' : 'raw',
  }
}

/** A one-line title: the type, at the crashing frame when there is one. */
export function issueTitle(issue: IssueSummary): string {
  return issue.type || issue.message || issue.id
}

export interface Share {
  label: string
  count: number
  share: number
}

function shares(entries: Map<string, number>, total: number, top: number): Share[] {
  const sorted = [...entries].sort((a, b) => b[1] - a[1])
  const head = sorted
    .slice(0, top)
    .map(([label, count]) => ({ label, count, share: count / total }))
  const rest = sorted.slice(top).reduce((n, [, c]) => n + c, 0)
  return rest > 0 ? [...head, { label: '', count: rest, share: rest / total }] : head
}

/** Occurrences by device model and by OS version, top three each plus the rest. */
export function breakdowns(detail: IssueDetail, top = 3): { devices: Share[]; os: Share[] } {
  const devices = new Map<string, number>()
  const os = new Map<string, number>()
  let total = 0
  for (const d of detail.devices) {
    total += d.occurrences
    devices.set(d.device_model || '?', (devices.get(d.device_model || '?') ?? 0) + d.occurrences)
    const v = `${d.os_name} ${d.os_version}`.trim() || '?'
    os.set(v, (os.get(v) ?? 0) + d.occurrences)
  }
  if (total === 0) return { devices: [], os: [] }
  return { devices: shares(devices, total, top), os: shares(os, total, top) }
}
